/**
 * What is printed (M7-6): a small document of blocks, laid out by Dob Desktop with the bundled
 * fonts (IBM Plex Sans Arabic, Inter; tabular figures) and sent to the printer as a picture
 * (ESC/POS raster), because most thermal printers cannot join Arabic letters (plan §3). The same
 * blocks will make receipts and kitchen tickets in Phase 5.
 */
import { z } from 'zod';

import type { PaperWidth, Printer } from './printers.ts';

const Text = z.string().max(2_000);
const Align = z.enum(['start', 'center', 'end']);
/** `huge`: an order number read from across the kitchen (O45). */
const Size = z.enum(['small', 'normal', 'large', 'xlarge', 'huge']);

export const PrintBlockSchema = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('text'),
    text: Text,
    align: Align.optional(),
    size: Size.optional(),
    bold: z.boolean().optional(),
    /** Base direction; `auto` follows the first strong letter (Arabic → right to left). */
    dir: z.enum(['auto', 'rtl', 'ltr']).optional(),
    /**
     * White on a black band across the paper. Large solid black prints striped and faded on older
     * thermal printers (seen on the Baghdad Road kitchen-left printer, O46): never for anything a
     * cook must read — kitchen tickets mark modifiers with «◄» and `indent` instead.
     */
    invert: z.boolean().optional(),
    /** Steps in from the start (right in Arabic), e.g. a modifier under its item (O46). */
    indent: z.number().int().min(0).max(3).optional(),
  }),
  /** A label and a value on one line (e.g. an item and its price), the value in tabular figures. */
  z.strictObject({
    kind: z.literal('row'),
    start: Text,
    end: Text,
    bold: z.boolean().optional(),
    size: Size.optional(),
  }),
  /** `thin`: a light separator for compact pages (kitchen tickets, O46). */
  z.strictObject({
    kind: z.literal('rule'),
    style: z.enum(['solid', 'dashed', 'thin']).optional(),
  }),
  z.strictObject({
    kind: z.literal('qr'),
    data: z.string().min(1).max(500),
    /** Width of the code in millimetres (default 30). */
    sizeMm: z.number().int().min(15).max(60).optional(),
    caption: Text.optional(),
  }),
  z.strictObject({ kind: z.literal('feed'), lines: z.number().int().min(1).max(10) }),
  /** Test page only: marks at both edges of the printable width and a centimetre scale. */
  z.strictObject({ kind: z.literal('ruler') }),
  /**
   * The Dob Burger logo in black (assets/brand `logo-en-ar-black.svg`), centred — always the bundled
   * file, never a picture sent in the document (O45: kitchen tickets and receipts).
   */
  z.strictObject({ kind: z.literal('logo'), widthMm: z.number().int().min(20).max(72).optional() }),
]);
export type PrintBlock = z.infer<typeof PrintBlockSchema>;

export const PrintDocumentSchema = z.strictObject({
  v: z.literal(1),
  /** The page's language (numbers and default direction). */
  lang: z.enum(['ar', 'en']),
  blocks: z.array(PrintBlockSchema).min(1).max(500),
  /** Cut the paper at the end (default true). */
  cut: z.boolean().optional(),
  /**
   * Tight lines and no space between blocks: every centimetre of paper counts on a kitchen ticket
   * printed hundreds of times a day (O46).
   */
  compact: z.boolean().optional(),
});
export type PrintDocument = z.infer<typeof PrintDocumentSchema>;

export interface TestPageInfo {
  readonly printer: Pick<Printer, 'name' | 'host' | 'port' | 'paperWidthMm'>;
  readonly locationName: { readonly ar: string; readonly en: string };
  readonly deviceName: string;
  /** When it was requested, already formatted in Baghdad time. */
  readonly time: string;
  readonly appVersion: string;
  /** Who asked (null: nobody signed in on the PC). */
  readonly requestedBy: string | null;
}

/** In Arabic only: a line mixing both directions comes out in a confusing order. */
const rulerNote: Record<PaperWidth, string> = {
  80: '72 مم = 576 نقطة',
  58: '48 مم = 384 نقطة',
};

/**
 * The test page (plan §3): Arabic and English, joined letters, tabular numbers, a QR code, the full
 * printable width, and a cut — everything a receipt will need, so one page shows whether the
 * printer, the paper width and the address are right.
 */
export function testPageDocument(info: TestPageInfo): PrintDocument {
  const { printer } = info;
  return {
    v: 1,
    lang: 'ar',
    cut: true,
    blocks: [
      { kind: 'text', text: 'دوب بركر', align: 'center', size: 'xlarge', bold: true },
      { kind: 'text', text: 'Dob Burger', align: 'center', size: 'large', bold: true, dir: 'ltr' },
      { kind: 'text', text: 'صفحة تجريبية للطابعة', align: 'center', size: 'large' },
      { kind: 'text', text: 'Printer test page', align: 'center', dir: 'ltr' },
      { kind: 'rule' },
      { kind: 'row', start: 'الموقع', end: info.locationName.ar },
      { kind: 'row', start: 'الطابعة', end: printer.name },
      { kind: 'row', start: 'العنوان', end: `${printer.host}:${String(printer.port)}` },
      { kind: 'row', start: 'عرض الورق', end: `${String(printer.paperWidthMm)} مم` },
      { kind: 'row', start: 'الجهاز', end: info.deviceName },
      { kind: 'row', start: 'الوقت', end: info.time },
      ...(info.requestedBy
        ? [{ kind: 'row' as const, start: 'طلبها', end: info.requestedBy }]
        : []),
      { kind: 'rule', style: 'dashed' },
      {
        kind: 'text',
        text: 'إذا كانت الحروف العربية متصلة وواضحة، والأرقام في أعمدة مستقيمة، فالطابعة جاهزة.',
      },
      {
        kind: 'text',
        text: 'If the Arabic letters are joined and clear and the numbers line up, the printer is ready.',
        dir: 'ltr',
        size: 'small',
      },
      { kind: 'rule', style: 'dashed' },
      { kind: 'row', start: 'دوبل دوب بركر × 2', end: '18,000' },
      { kind: 'row', start: 'بطاطا كبيرة × 1', end: '3,500' },
      { kind: 'row', start: 'مشروب غازي × 3', end: '4,500' },
      { kind: 'rule' },
      { kind: 'row', start: 'المجموع (د.ع)', end: '26,000', bold: true, size: 'large' },
      { kind: 'text', text: 'Total IQD 26,000 — 0123456789', dir: 'ltr', align: 'center' },
      { kind: 'qr', data: 'https://dobburger.com', caption: 'dobburger.com' },
      {
        kind: 'text',
        text: `عرض الطباعة: ${rulerNote[printer.paperWidthMm]}`,
        align: 'center',
        size: 'small',
      },
      { kind: 'ruler' },
      {
        kind: 'text',
        text: `Dob Desktop ${info.appVersion}`,
        align: 'center',
        size: 'small',
        dir: 'ltr',
      },
      { kind: 'text', text: '— نهاية الصفحة التجريبية —', align: 'center' },
    ],
  };
}
